import axios from "axios";
import { supabase } from "../supabaseClient";

export const axiosInstance = axios.create({
  baseURL: process.env.NODE_ENV === "production" ? "https://api.22night.com" : "http://localhost:8000",
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// Single in-flight refresh shared by every 401 handler and request interceptor.
// GoTrue rotates the refresh token on every refresh, so concurrent
// refreshSession() calls race: the loser presents a rotated/stale token and
// GoTrue rejects it (e.g. "InvalidJWTToken: Invalid value for JWT claim exp").
// One shared promise means exactly ONE refresh per wave of 401s, and every
// caller (request interceptor included) waits on it before using a token.
let refreshPromise = null;

// Returns the current session, first waiting for any refresh already in
// flight, so callers never attach an about-to-be-rotated (stale) access token.
async function getCurrentSession() {
  if (refreshPromise) {
    try {
      await refreshPromise;
    } catch {
      // Refresh failed; fall through and read whatever is stored now.
    }
  }
  const { data: { session } } = await supabase.auth.getSession();
  return session;
}

// Single-flight refresh: all concurrent 401s share this one refresh attempt.
// Returns the refreshed session, or null when there is no session to refresh
// or the refresh is unrecoverable (dead refresh token) — in that case the
// stored session is cleared once so nothing keeps retrying with it.
function refreshSessionOnce() {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const { data: { session: current } } = await supabase.auth.getSession();
        if (!current?.refresh_token) {
          return null;
        }
        // Passing the session forces a real GoTrue refresh. Without the
        // argument, refreshSession() returns the stored session unchanged when
        // it isn't locally expired — but a 401 means the server already
        // rejected this token, so we must mint a new one.
        const { data: { session }, error } = await supabase.auth.refreshSession(current);
        if (error || !session?.access_token) {
          // Unrecoverable refresh (e.g. refresh token expired -> InvalidJWTToken).
          // Clear the stored session so every page load stops retrying it.
          await supabase.auth.signOut({ scope: "local" }).catch(() => {});
          return null;
        }
        return session;
      } catch {
        return null;
      }
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

// Attach the CURRENT Supabase access token to every request. If a refresh is
// already in flight, wait for it so we never send an expired/stale token.
axiosInstance.interceptors.request.use(async (config) => {
  const session = await getCurrentSession();
  if (session?.access_token) {
    config.headers.Authorization = `Bearer ${session.access_token}`;
  }
  return config;
});

// On 401 responses, refresh the session once (single-flight, shared by all
// concurrent 401s) and retry the original request with the fresh token.
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;
      const session = await refreshSessionOnce();
      if (session?.access_token) {
        originalRequest.headers.Authorization = `Bearer ${session.access_token}`;
        return axiosInstance(originalRequest);
      }
      // Refresh failed and the dead session was cleared — reject once, no
      // retry loop. The app falls back to its normal signed-out flow via the
      // SIGNED_OUT auth event.
    }
    return Promise.reject(error);
  }
);
